import SectionIcon from '@components/SectionIcon';
import {
  Badge,
  Button,
  Flex,
  Progress,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  Text,
} from '@retracedgmbh/ui-components-lib';
import { useState } from 'react';
import useFeature from '@/hooks/useFeature';
import type { UploadFileData } from '@/hooks/useFileUploadNew';
import useTranslator from '@/hooks/useTranslator';
import type {
  ClassifyResultItem,
  FileClassificationType,
} from '@/services/api/types/ai-processing';
import { isCapImportContextMissing, isItemReviewable } from './auditStages';
import BulkFileUpload from './BulkFileUpload';
import QueueSection from './QueueSection';
import type { AiProcessingItem, AiProcessingSummary } from './types';

interface AIProcessingSheetProps {
  isOpen: boolean;
  onClose: () => void;
  data: AiProcessingSummary | undefined;
  isLoading: boolean;
  onCta: (item: AiProcessingItem) => void;
  bulkUploadBatchKey: number;
  bulkUploadFiles: UploadFileData[];
  onBulkUploadFinish: (files: UploadFileData[]) => void;
  isBulkClassifying: boolean;
  classifyResults?: ClassifyResultItem[];
  typeOverrides: Record<string, FileClassificationType>;
  onTypeChange: (fileId: string, type: FileClassificationType) => void;
  onAccept: () => Promise<boolean>;
  isAccepting: boolean;
  onCancel: () => void;
  isPolling: boolean;
}

const TRANSLATION_PAGE = 'ai-processing';

export default function AIProcessingSheet({
  isOpen,
  onClose,
  data,
  isLoading,
  onCta,
  bulkUploadBatchKey,
  bulkUploadFiles,
  onBulkUploadFinish,
  isBulkClassifying,
  classifyResults,
  typeOverrides,
  onTypeChange,
  onAccept,
  isAccepting,
  onCancel,
  isPolling,
}: AIProcessingSheetProps) {
  const { isDropBoxEnabled } = useFeature();
  const { getTranslation } = useTranslator({ page: TRANSLATION_PAGE });
  const [activeTab, setActiveTab] = useState('queue');

  const stats = data?.stats;
  const items = data?.items ?? [];

  // Sections: a card is "Ready to review" as soon as any of its stages is
  // finished (isItemReviewable) — even while other stages keep processing — so a
  // partly-done audit surfaces for review instead of staying under "In progress".
  // "In progress" is therefore the processing cards that have nothing to review
  // yet.
  const failedItems = items.filter(item => item.status === 'FAILED');
  const reviewItems = items.filter(isItemReviewable);
  const inProgressItems = items.filter(
    item => item.status === 'PROCESSING' && !isItemReviewable(item),
  );
  // A CAP-only import whose row never recorded where the import belongs is NOT
  // reviewable (`isItemReviewable` is false, so it is absent from `reviewItems`
  // and from every "ready to review" count), but it is finished — it matches no
  // other section either, and the user must still be able to dismiss it. So it
  // renders in the review section behind a disabled Review CTA (see `QueueCard`),
  // without ever being counted as actionable work.
  const blockedItems = items.filter(isCapImportContextMissing);
  const reviewSectionItems = [...reviewItems, ...blockedItems];
  // Summary header state. Any failure turns the bar red (even while other jobs
  // still run) and shows a "N failed" badge alongside the processing / ready
  // counts — all counts stay visible at once, and the aggregate % keeps
  // advancing. ("Stopped" is reserved for a broken summary endpoint — see the
  // open item; not wired to a job-failure anymore.)
  const anyProcessing = (stats?.processing ?? 0) > 0;
  const hasFailed = (stats?.failed ?? 0) > 0;

  const hasFiles = bulkUploadFiles.length > 0;
  const allClassified = classifyResults?.length
    ? classifyResults.every(
        r => r.status === 'completed' || r.status === 'failed',
      )
    : false;
  const classifiedCount =
    classifyResults?.filter(r => r.status === 'completed').length ?? 0;

  const handleStartProcessing = async () => {
    const success = await onAccept();
    if (success) {
      setActiveTab('queue');
    }
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  // Queue body:
  //  - loading → spinner
  //  - empty   → "Processing jobs:" header + "No active jobs" empty state
  //  - jobs    → summary header + "Failed" / "Ready to review" / "In progress" sections
  const queueContent = isLoading ? (
    <Flex justify='center' padding='2xl'>
      <i className='fa-light fa-circle-notch tw:animate-spin tw:text-content-secondary tw:text-2xl' />
    </Flex>
  ) : items.length === 0 ? (
    <Flex direction='column' gap='2xl' className='tw:py-6'>
      {/* "Processing jobs:" header with the processing spinner badge */}
      <Flex alignItems='center' gap='md'>
        <SectionIcon
          icon='fa-light fa-circle-notch'
          iconClassName='tw:animate-spin'
          type='primary'
          size='md'
        />
        <Text size='base' weight='semibold'>
          {getTranslation('queue.processingJobs')}
        </Text>
      </Flex>

      {/* Empty state */}
      <Flex
        direction='column'
        alignItems='center'
        gap='sm'
        className='tw:py-12'
      >
        <i className='fa-regular fa-list tw:text-content-secondary tw:text-2xl' />
        <Text color='secondary' weight='semibold'>
          {getTranslation('queue.noActiveJobs')}
        </Text>
        <Text size='small' className='tw:text-content-tertiary tw:text-center'>
          {getTranslation('queue.noActiveJobsHint')}
        </Text>
      </Flex>
    </Flex>
  ) : (
    <Flex direction='column' gap='2xl' className='tw:py-6'>
      {/* Summary header — the icon sits in a left gutter; the title, badges and
          the progress bar share a right column so the bar aligns under the
          title (not under the icon). */}
      <Flex gap='md' alignItems='start'>
        <SectionIcon
          icon={
            hasFailed
              ? 'fa-regular fa-triangle-exclamation'
              : anyProcessing
                ? 'fa-light fa-circle-notch'
                : 'fa-regular fa-circle-check'
          }
          iconClassName={
            !hasFailed && anyProcessing ? 'tw:animate-spin' : undefined
          }
          // Failed = red (takes priority so the failure is always visible);
          // processing = brand ('primary'); all done = amber.
          type={
            hasFailed ? 'destructive' : anyProcessing ? 'primary' : 'warning'
          }
          size='md'
        />
        <Flex direction='column' gap='sm' className='tw:flex-1 tw:min-w-0'>
          <Flex alignItems='center' gap='md' wrap='wrap'>
            <Text size='base' weight='semibold'>
              {getTranslation('queue.processingJobs')}
            </Text>
            {stats ? (
              <Flex gap='xs'>
                {stats.processing > 0 && (
                  <Badge type='primary' radius='squared'>
                    {getTranslation('queue.processing', {
                      count: stats.processing,
                    })}
                  </Badge>
                )}
                {/* "Ready for review" counts jobs with an unreviewed reviewable
                    stage (reviewItems / isItemReviewable), matching the section
                    below — NOT stats.completed, which counts every finished job
                    (including already-reviewed ones) and so over-counts. */}
                {reviewItems.length > 0 && (
                  <Badge type='warning' radius='squared'>
                    <Text className='tw:text-amber-800'>
                      {getTranslation('queue.readyForReview', {
                        count: reviewItems.length,
                      })}
                    </Text>
                  </Badge>
                )}
                {stats.failed > 0 && (
                  <Badge type='destructive' radius='squared'>
                    {getTranslation('queue.failed', { count: stats.failed })}
                  </Badge>
                )}
              </Flex>
            ) : null}
          </Flex>
          {stats ? (
            <Flex alignItems='center' gap='sm'>
              {/* Progress's fill is hardcoded brand; recolor the indicator to
                  red for the failed state via the child-variant (same pattern
                  as OrderTableCells). The bar keeps showing the aggregate %. */}
              <Flex className='tw:flex-1'>
                <Progress
                  className={
                    hasFailed
                      ? 'tw:h-2 tw:[&>*]:bg-background-negative'
                      : 'tw:h-2'
                  }
                  value={stats.percentage}
                />
              </Flex>
              <Text
                size='small'
                weight='semibold'
                className={
                  hasFailed
                    ? 'tw:text-content-negative tw:whitespace-nowrap'
                    : 'tw:text-content-secondary tw:whitespace-nowrap'
                }
              >
                {getTranslation('queue.percentComplete', {
                  percentage: stats.percentage,
                })}
              </Text>
            </Flex>
          ) : null}
        </Flex>
      </Flex>

      <QueueSection variant='failed' items={failedItems} onCta={onCta} />
      {/* `reviewSectionItems` (not `reviewItems`) so a blocked CAP import stays
          visible and dismissible — see the filters above. */}
      <QueueSection variant='review' items={reviewSectionItems} onCta={onCta} />
      <QueueSection
        variant='inProgress'
        items={inProgressItems}
        onCta={onCta}
      />
    </Flex>
  );

  const tabItems = [
    {
      key: 'queue',
      label: (
        <Flex gap='sm' alignItems='center'>
          <i
            className={`${activeTab === 'queue' ? 'fa-solid' : 'fa-regular'} fa-sparkles`}
          />
          {getTranslation('tabs.queue')}
        </Flex>
      ),
      children: queueContent,
    },
    ...(isDropBoxEnabled
      ? [
          {
            key: 'upload',
            label: (
              <Flex gap='sm' alignItems='center'>
                <i
                  className={`${activeTab === 'upload' ? 'fa-solid' : 'fa-regular'} fa-cloud-arrow-up`}
                />
                {getTranslation('tabs.upload')}
                {classifiedCount > 0 && (
                  <span className='tw:flex tw:items-center tw:justify-center tw:min-w-5 tw:h-5 tw:rounded-full tw:bg-content-info-ai-bold tw:px-1'>
                    <Text
                      as='span'
                      className='tw:text-xs tw:leading-none tw:text-white tw:font-semibold'
                    >
                      {classifiedCount}
                    </Text>
                  </span>
                )}
              </Flex>
            ),
            children: (
              <Flex direction='column' padding='2xl'>
                <BulkFileUpload
                  batchKey={bulkUploadBatchKey}
                  uploadedFiles={bulkUploadFiles}
                  onUploadFinish={onBulkUploadFinish}
                  isDisabled={isBulkClassifying}
                  classifyResults={classifyResults}
                  typeOverrides={typeOverrides}
                  onTypeChange={onTypeChange}
                />
              </Flex>
            ),
          },
        ]
      : []),
  ];

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        onOpenAutoFocus={e => e.preventDefault()}
        className='tw:w-[568px] tw:max-w-[568px] tw:p-0 tw:flex tw:flex-col tw:overflow-hidden'
      >
        <SheetHeader className='tw:p-6 tw:pb-0 tw:shrink-0'>
          <Flex direction='column' gap='xs'>
            <SheetTitle className='tw:mb-3'>
              {getTranslation('sheet.title')}
            </SheetTitle>
            <Text size='small' color='secondary'>
              {getTranslation('sheet.description')}
            </Text>
          </Flex>
        </SheetHeader>

        <Flex
          direction='column'
          className='tw:flex-1 tw:overflow-y-auto tw:min-h-0'
        >
          {/* Tabs only exist to host the drop-box bulk-upload tab — when that
              feature is off there's a single view, so render the queue directly
              (no tab chrome). `tw:px-6` mirrors the inset the Tabs added. */}
          {isDropBoxEnabled ? (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              className='tw:px-6'
            />
          ) : (
            <Flex direction='column' className='tw:px-6'>
              {/* With tabs on, the Tabs strip's bottom border separates the
                  header from the body; without tabs, add that divider here. */}
              <Separator className='tw:my-3' />
              {queueContent}
            </Flex>
          )}
        </Flex>

        {activeTab === 'upload' && hasFiles && (
          <Flex
            justify='end'
            alignItems='center'
            gap='md'
            padding='2xl'
            className='tw:border-t tw:border-border-primary tw:shrink-0'
          >
            <Button variant='ghost' onClick={handleCancel}>
              {getTranslation('upload.cancel')}
            </Button>
            <Button
              variant='primary'
              onClick={handleStartProcessing}
              disabled={!allClassified || isAccepting || isPolling}
              loading={isAccepting}
              icon='fa-regular fa-arrow-right'
              iconPosition='right'
            >
              {getTranslation('upload.startProcessing')}
            </Button>
          </Flex>
        )}
      </SheetContent>
    </Sheet>
  );
}
